import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useState } from 'react';

interface RiskPoint {
  id: string;
  name: string;
  likelihood: number; // 1-5
  impact: number; // 1-5
  category: string;
}

interface LikelihoodImpactMatrixProps {
  risks: RiskPoint[];
  className?: string;
}

const getRiskZone = (likelihood: number, impact: number): string => {
  const score = likelihood * impact;
  if (score >= 20) return 'critical';
  if (score >= 12) return 'high';
  if (score >= 6) return 'medium';
  return 'low';
};

const getZoneColor = (zone: string): string => {
  switch (zone) {
    case 'critical':
      return 'bg-red-600/20 border-red-600';
    case 'high':
      return 'bg-orange-600/20 border-orange-600';
    case 'medium':
      return 'bg-yellow-600/20 border-yellow-600';
    case 'low':
      return 'bg-green-700/20 border-green-700';
    default:
      return 'bg-neutral-800/20 border-neutral-700';
  }
};

const categoryColors: Record<string, string> = {
  Operational: 'bg-blue-500',
  Financial: 'bg-emerald-500',
  Strategic: 'bg-purple-500',
  Compliance: 'bg-cyan-500',
  Reputational: 'bg-pink-500',
};

export function LikelihoodImpactMatrix({ risks, className }: LikelihoodImpactMatrixProps) {
  const [hoveredRisk, setHoveredRisk] = useState<string | null>(null);

  return (
    <Card className={cn("p-6", className)}>
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-white">Likelihood vs Impact Matrix</h3>
        <p className="text-sm text-neutral-400 mt-1">Risk classification by probability and consequence</p>
      </div>

      <div className="relative">
        {/* Matrix Grid */}
        <div className="relative w-full aspect-square border-l-2 border-b-2 border-neutral-700">
          {/* Quadrant Background Zones */}
          <div className="absolute inset-0 grid grid-cols-5 grid-rows-5">
            {[...Array(25)].map((_, idx) => {
              const row = Math.floor(idx / 5);
              const col = idx % 5;
              const likelihood = col + 1;
              const impact = 5 - row;
              const zone = getRiskZone(likelihood, impact);
              
              return (
                <div
                  key={idx}
                  className={cn(
                    "border-r border-t border-neutral-800/50",
                    getZoneColor(zone)
                  )}
                />
              );
            })}
          </div>

          {/* Risk Points */}
          <div className="absolute inset-0">
            {risks.map((risk) => {
              const x = ((risk.likelihood - 0.5) / 5) * 100;
              const y = ((5 - risk.impact + 0.5) / 5) * 100;
              const isHovered = hoveredRisk === risk.id;
              
              return (
                <div
                  key={risk.id}
                  className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer"
                  style={{ left: `${x}%`, top: `${y}%` }}
                  onMouseEnter={() => setHoveredRisk(risk.id)}
                  onMouseLeave={() => setHoveredRisk(null)}
                >
                  <div
                    className={cn(
                      "w-3 h-3 rounded-full border-2 border-white shadow-lg transition-all duration-200",
                      categoryColors[risk.category] || 'bg-neutral-500',
                      isHovered && "scale-150 ring-4 ring-white/30"
                    )}
                  />
                  
                  {/* Tooltip */}
                  {isHovered && (
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-neutral-900 border border-neutral-700 rounded-lg shadow-xl whitespace-nowrap z-10">
                      <p className="text-xs font-semibold text-white">{risk.name}</p>
                      <p className="text-xs text-neutral-400 mt-1">
                        Likelihood: {risk.likelihood} | Impact: {risk.impact}
                      </p>
                      <p className="text-xs text-neutral-500">{risk.category}</p>
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-px">
                        <div className="border-4 border-transparent border-t-neutral-700" />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Axis Labels */}
        <div className="mt-4 flex justify-center">
          <div className="text-center">
            <p className="text-sm font-medium text-neutral-300">Likelihood →</p>
            <div className="flex gap-8 mt-1">
              <span className="text-xs text-neutral-500">Rare</span>
              <span className="text-xs text-neutral-500">Unlikely</span>
              <span className="text-xs text-neutral-500">Possible</span>
              <span className="text-xs text-neutral-500">Likely</span>
              <span className="text-xs text-neutral-500">Certain</span>
            </div>
          </div>
        </div>

        <div className="absolute left-0 top-1/2 transform -translate-x-full -translate-y-1/2 -rotate-90 origin-center">
          <p className="text-sm font-medium text-neutral-300 whitespace-nowrap">Impact →</p>
        </div>

        {/* Legend */}
        <div className="mt-6 pt-6 border-t border-neutral-800">
          <p className="text-xs font-medium text-neutral-400 mb-3">Risk Categories</p>
          <div className="flex flex-wrap gap-4">
            {Object.entries(categoryColors).map(([category, color]) => (
              <div key={category} className="flex items-center gap-2">
                <div className={cn("w-3 h-3 rounded-full border-2 border-white", color)} />
                <span className="text-xs text-neutral-400">{category}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}
